/* eslint-disable no-undef */
import React from 'react';
import PropTypes from 'prop-types';
import { AUDIO_MUTE_BUTTON_ENABLED } from 'store/modules/base/flags/constants';
import { getFeatureFlag } from 'store/modules/base/flags/functions';
import { translate } from 'store/modules/base/i18n/functions';
import { MEDIA_TYPE } from 'store/modules/base/media/constants';
import { getLocalParticipant } from 'store/modules/base/participants/functions';
import { connect } from 'store/modules/base/redux/functions';
import { isLocalTrackMuted } from 'store/modules/base/tracks/functions';
import { isAudioMuteButtonDisabled } from 'store/modules/toolbox/functions';
import { muteLocal } from 'store/modules/video-menu/actions';
import { sendMessage } from 'store/modules/chat/actions';
import {
  ACTION_SHORTCUT_TRIGGERED,
  AUDIO_MUTE,
  createShortcutEvent
} from 'store/modules/analytics/AnalyticsEvents';
import { sendAnalytics } from 'store/modules/analytics/functions';
import {
  registerShortcut,
  unregisterShortcut
} from 'store/modules/keyboard-shortcuts/actions';
import { MicIcon, Unmute } from 'assets/images';
import AbstractButton from './AbstractButton';
import withBoundary from 'component/ErrorBoundary/withBoundary';

/**
 * Button used for audio & audio settings.
 *
 * @returns {ReactElement}
 */
class AudioSettingsButton extends AbstractButton {
  accessibilityLabel = 'toolbar.accessibilityLabel.mute';
  label = 'toolbar.muteMic';
  toggledLabel = 'toolbar.unmuteMic';
  type = 'darkgrey';
  icon = MicIcon;
  toggledIcon = Unmute;

  /**
   * Initializes a new {@code AudioSettingsButton} instance.
   *
   * @inheritdoc
   */
  constructor(props) {
    super(props);

    this._onEscClick = this._onEscClick.bind(this);
    // Bind event handlers so they are only bound once per instance.
    this._onKeyboardShortcut = this._onKeyboardShortcut.bind(this);
  }

  /**
   * Click handler for the more actions entries.
   *
   * @param {KeyboardEvent} event - Esc key click to close the popup.
   * @returns {void}
   */
  _onEscClick(event) {
    if (event.key === 'Escape' && this.props.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      this._onClick();
    }
  }

  /**
   * Registers the keyboard shortcut that toggles the audio muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {
    this.props.dispatch(
      registerShortcut({
        character: 'T',
        helpDescription: 'keyboardShortcuts.mute',
        handler: this._onKeyboardShortcut
      })
    );
  }

  /**
   * Unregisters the keyboard shortcut that toggles the audio muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentWillUnmount() {
    this.props.dispatch(unregisterShortcut('T'));
  }

  /**
   * Indicates if audio is currently muted or not.
   *
   * @override
   * @protected
   * @returns {boolean}
   */
  _isAudioMuted() {
    return this.props._audioMuted;
  }

  /**
   * Creates an analytics keyboard shortcut event and dispatches an action to
   * toggle the audio muting.
   *
   * @private
   * @returns {void}
   */
  _onKeyboardShortcut() {
    // Ignore keyboard shortcuts if the audio button is disabled.
    if (this._isDisabled()) {
      return;
    }

    sendAnalytics(
      createShortcutEvent(AUDIO_MUTE, ACTION_SHORTCUT_TRIGGERED, {
        enable: !this._isAudioMuted()
      })
    );

    AbstractButton.prototype._onClick.call(this);
  }

  /**
   * Changes the muted state.
   *
   * @param {boolean} audioMuted - Whether audio should be muted or not.
   * @protected
   * @returns {void}
   */
  _setAudioMuted(audioMuted) {
    this.props.dispatch(muteLocal(audioMuted, MEDIA_TYPE.AUDIO));
    this.props._conference.sendCommand('customAudioStat', {
      attributes: { xmlns: 'http://jabber.org/protocol/customAudioStat' },
      value: audioMuted ? 'on' : 'off'
    });
    this.props.dispatch(
      sendMessage(
        audioMuted
          ? JSON.stringify({
              EventType: 3,
              Message: 'Audio Off',
              senderID: this.props.localParticipant,
              FromParticipantID: this.props.localParticipant
            })
          : JSON.stringify({
              EventType: 4,
              Message: 'Audio On',
              senderID: this.props.localParticipant,
              FromParticipantID: this.props.localParticipant
            })
      )
    );
  }

  /**
   * Return a boolean value indicating if this button is disabled or not.
   *
   * @returns {boolean}
   */
  _isDisabled() {
    return this.props._disabled;
  }

  /**
   * Indicates whether this button is in toggled state or not.
   *
   * @override
   * @protected
   * @returns {boolean}
   */
  _isToggled() {
    return this._isAudioMuted();
  }

  /**
   * Handles clicking / pressing the button, and toggles the audio mute state
   * accordingly.
   *
   * @override
   * @protected
   * @returns {void}
   */
  _handleClick() {
    this._setAudioMuted(!this._isAudioMuted());
  }
}

/**
 * Function that maps parts of Redux state tree into component props.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
  const _audioMuted = isLocalTrackMuted(
    state['features/base/tracks'],
    MEDIA_TYPE.AUDIO
  );
  const _conference = state['features/base/conference'].conference;
  const localParticipant = getLocalParticipant(state)?.id;
  const _disabled = isAudioMuteButtonDisabled(state);
  const enabledFlag = getFeatureFlag(state, AUDIO_MUTE_BUTTON_ENABLED, true);

  return {
    _audioMuted,
    _conference,
    _disabled,
    localParticipant,
    _userIdleMode: state['features/settings'].userIdleMode,
    visible: enabledFlag
  };
}

AudioSettingsButton.propTypes = {
  /**
   * The button's key.
   */
  buttonKey: PropTypes.string,

  /**
   * External handler for click action.
   */
  handleClick: PropTypes.func,

  /**
   * Indicates whether audio permissions have been granted or denied.
   */
  hasPermissions: PropTypes.bool,

  /**
   * Click handler for the small icon. Opens audio options.
   */
  onAudioOptionsClick: PropTypes.func,

  /**
   * If the button should be disabled.
   */
  isDisabled: PropTypes.bool,
  isDesktop: PropTypes.bool,

  /**
   * Notify mode for `toolbarButtonClicked` event -
   * whether to only notify or to also prevent button click routine.
   */
  notifyMode: PropTypes.string,

  /**
   * Used for translation.
   */
  t: PropTypes.func,

  /**
   * Flag controlling the visibility of the button.
   * AudioSettings popup is disabled on mobile browsers.
   */
  visible: PropTypes.bool,

  /**
   * Defines is popup is open.
   */
  isOpen: PropTypes.bool
};
export default React.memo(
  translate(connect(mapStateToProps)(withBoundary(AudioSettingsButton)))
);
